import React from 'react'
import { Loader, Users } from 'lucide-react'
import { cn } from '@/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { OrgRole } from '@/types/organization'
import {
  MemberAvatar,
  ORG_DANGER_BUTTON,
  ORG_GHOST_BUTTON,
  ORG_SELECT_CONTENT,
  ORG_SELECT_ITEM,
  ORG_SELECT_TRIGGER,
  ORG_SELECT_TRIGGER_SM,
  ORG_TD,
  ORG_TH,
  OrgEmptyState,
  RoleBadge,
  SelfBadge,
  memberName,
} from '../OrgPrimitives'
import {
  OrgMembersListBaseProps,
  ROLE_LABELS,
  ROLE_OPTIONS,
  canChangeRoleOfRow,
  canRemoveRow,
  canViewActivity,
} from './OrgMembersListBase'

/**
 * Desktop members table.
 *
 * The activity columns (Bids / RFQs / Total) and the Actions column only exist
 * for OWNER/ADMIN — a plain MEMBER sees the roster and nothing else.
 */
export const OrgMembersListDesktop: React.FC<OrgMembersListBaseProps> = (props) => {
  const { rows, isLoading, canManage, selectedUserId, pendingUserId } = props
  const showActivity = canViewActivity(props)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10">
        <Loader className="animate-spin text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" size={20} />
        <span className="font-plus-jakarta text-[14px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          Loading members...
        </span>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <OrgEmptyState
        icon={Users}
        title="No members yet"
        description="Invite a colleague to get started."
        className="py-8"
      />
    )
  }

  return (
    <div className="overflow-x-auto dark:bg-[rgba(10,10,10,0.2)]">
      <table className="w-full min-w-[880px] border-collapse">
        <thead>
          <tr className="h-10 border-b border-[rgba(45,41,35,0.12)] dark:border-[#0a0a0a] dark:bg-[rgba(28,28,28,0.32)]">
            <th className={cn(ORG_TH, 'w-[280px]')}>Member</th>
            <th className={cn(ORG_TH, 'w-[260px]')}>Email</th>
            <th className={cn(ORG_TH, 'w-[160px]')}>Role</th>
            {showActivity && (
              <>
                <th className={cn(ORG_TH, 'w-[90px]')}>Bids</th>
                <th className={cn(ORG_TH, 'w-[90px]')}>RFQs</th>
                <th className={cn(ORG_TH, 'w-[90px]')}>Total</th>
              </>
            )}
            {canManage && <th className={cn(ORG_TH, 'w-[280px]')}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const isStriped = index % 2 === 0
            const isSelected = selectedUserId === row.membership.user_id
            const isPending = pendingUserId === row.membership.user_id
            const showRoleSelect = canChangeRoleOfRow(row, props)
            const showRemove = canRemoveRow(row, props)

            return (
              <tr
                key={row.membership.id}
                className={cn(
                  isStriped
                    ? 'bg-[rgba(255,255,255,0.28)] dark:bg-[rgba(28,28,28,0.8)]'
                    : 'bg-transparent',
                  isSelected && 'ring-1 ring-inset ring-[rgba(45,41,35,0.32)] dark:ring-[rgba(253,253,253,0.32)]',
                )}
              >
                <td className={ORG_TD}>
                  <div className="flex items-center gap-[10px]">
                    <MemberAvatar member={row.membership} />
                    <span className="truncate font-medium">{memberName(row.membership)}</span>
                    {row.isSelf && <SelfBadge />}
                  </div>
                </td>

                <td className={cn(ORG_TD, 'text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]')}>
                  <span className="truncate">{row.membership.user?.email ?? '—'}</span>
                </td>

                <td className={ORG_TD}>
                  {showRoleSelect ? (
                    <Select
                      value={row.membership.role}
                      disabled={isPending}
                      onValueChange={(value) => props.onChangeRole(row, value as OrgRole)}
                    >
                      <SelectTrigger className={cn(ORG_SELECT_TRIGGER, ORG_SELECT_TRIGGER_SM)}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={ORG_SELECT_CONTENT}>
                        {ROLE_OPTIONS.map((role) => (
                          <SelectItem key={role} value={role} className={ORG_SELECT_ITEM}>
                            {ROLE_LABELS[role]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <RoleBadge role={row.membership.role} />
                  )}
                </td>

                {showActivity && (
                  <>
                    <td className={ORG_TD}>{row.counts?.bids ?? 0}</td>
                    <td className={ORG_TD}>{row.counts?.rfqs ?? 0}</td>
                    <td className={cn(ORG_TD, 'font-medium')}>{row.counts?.total ?? 0}</td>
                  </>
                )}

                {canManage && (
                  <td className={ORG_TD}>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => props.onViewActivity(row)}
                        className={ORG_GHOST_BUTTON}
                      >
                        {isSelected ? 'Hide' : 'View'}
                      </button>
                      {showRemove && (
                        <button
                          type="button"
                          onClick={() => props.onRemove(row)}
                          disabled={isPending}
                          className={ORG_DANGER_BUTTON}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default OrgMembersListDesktop
